<template>
  <gg-modal :is-active="isActive" @close="close">
    <template v-slot:toolbar>
      Add Alias
    </template>
    <template v-slot:body>
      <v-form class="pt-2" @submit.prevent="create">
        <v-row>
          <v-col cols="12" class="py-0">
            <gg-text-field
              outlined
              dense
              label="Alias Name"
              placeholder="ex. sales"
              autocomplete="off"
              v-model="model.alias"
              v-validate="'required'"
              name="Alias"
              :error-messages="errors.collect('Alias')"
            />
          </v-col>
          <v-col cols="12" class="py-0">
            <!-- An alias belongs to the same cPanel account as the mailbox it
                 delivers to, so the choice is between that account's own
                 domains. The mailbox's domain is preselected and stays valid
                 even when the list could not be read. -->
            <gg-select
              v-model="model.domain"
              label="Domain"
              :items="domains"
              :loading="loading"
              :disabled="loading"
              v-validate="'required'"
              data-vv-name="domain"
              data-vv-as="Domain"
              :error-messages="errors.collect('domain')"
            />
          </v-col>
          <v-col cols="12" class="py-0">
            <!-- Fixed, not a picker: aliases point only at mailboxes on the
                 account (AM-1104, Kaumil 2026-09-23), and this screen is always
                 opened from the mailbox the mail should reach. -->
            <gg-input-label>Delivers To</gg-input-label>
            <p class="body-2 mb-4">{{ mailbox.email }}</p>
          </v-col>
          <v-col v-if="address" cols="12" class="pt-0">
            <p class="body-2 mb-0">
              Mail sent to <strong>{{ address }}</strong> will be delivered to
              <strong>{{ mailbox.email }}</strong>.
            </p>
          </v-col>
        </v-row>
      </v-form>
    </template>
    <template v-slot:actions>
      <v-spacer></v-spacer>
      <gg-btn secondary @click="close">Cancel</gg-btn>
      <gg-btn primary :loading="saving" :disabled="loading" @click="create">
        Add Alias
      </gg-btn>
    </template>
  </gg-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'AddAliasModal',
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    mailbox: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      model: {
        alias: '',
        domain: '',
      },
      loading: false,
      saving: false,
    }
  },
  computed: {
    ...mapGetters('emailHub', ['mailboxDomainsForService']),
    domains () {
      const domains = this.mailboxDomainsForService(this.mailbox.serviceId)
      // Before the list arrives — or if it never does — the mailbox's own
      // domain is the one option we know to be good.
      return domains.length ? domains : [this.mailbox.domain]
    },
    address () {
      if (!this.model.alias || !this.model.domain) return ''
      return `${this.model.alias}@${this.model.domain}`
    },
  },
  methods: {
    ...mapActions('emailHub', ['loadDomainsForService', 'addAlias']),
    ...mapActions(['reportError', 'reportSuccess']),
    close () {
      this.$emit('close')
    },
    reset () {
      this.model.alias = ''
      this.model.domain = this.mailbox.domain
      this.$validator.reset()
    },
    /**
     * Everything the form needs on the way in.
     *
     * Called from both the `isActive` watcher and `created`: the dashboard
     * mounts this modal behind `v-if="selected"` and opens it in the same tick,
     * so the component is created with `isActive` already true and the watcher
     * never fires. The Aliases tab mounts it up front, where only the watcher
     * runs. Resetting here rather than on close also means reopening on a
     * different row never shows the previous row's input.
     */
    open () {
      this.reset()
      this.loadDomains()
    },
    /**
     * GGModal is `eager`, so this component exists before it is ever opened.
     * Read the domains on first open rather than on mount, the same way
     * CreateMailboxModal does.
     */
    async loadDomains () {
      this.loading = true
      try {
        await this.loadDomainsForService(this.mailbox.serviceId)
      } catch (e) {
        // Already recorded as a domain error, which the alert above renders.
        // The form still works on the mailbox's own domain.
        console.warn('AddAliasModal: unable to load domains for this service', e)
      } finally {
        this.loading = false
      }
    },
    async create () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      this.saving = true
      try {
        await this.addAlias({
          serviceId: this.mailbox.serviceId,
          dest: `${this.model.alias}@${this.model.domain}`,
          domain: this.model.domain,
          forward: this.mailbox.email,
        })
      } catch (e) {
        this.saving = false
        this.reportError(e)
        return
      }

      this.saving = false
      this.reportSuccess('Alias successfully added.')
      this.reset()
      this.$emit('created')
      this.close()
    },
  },
  watch: {
    isActive (active) {
      if (active) this.open()
    },
  },
  created () {
    if (this.isActive) this.open()
  },
}
</script>
